import { useMemo } from 'react';
import { uniqBy } from 'lodash-es';
import { LogisticsChangeLogType, SuggestedRoutesAppliedLog } from '@/api/apis/route';
import { ChangeLogRouteLocationsList } from './ChangeLogRouteLocationsList';
import { ChangeLogResourcesList } from './ChangeLogResourcesList';

type SuggestedRoutesLogProps = {
  log: SuggestedRoutesAppliedLog;
};

export const SuggestedRoutesLog = ({ log }: SuggestedRoutesLogProps) => {
  const hasResources = log.$type === LogisticsChangeLogType.SuggestedRoutesAppliedLog;

  const addedLegs = useMemo(() => log.addedLegs.map(({ leg }) => leg), [log.addedLegs]);
  const removedLegs = useMemo(() => log.removedLegs.map(({ leg }) => leg), [log.removedLegs]);

  const addedResources = useMemo(
    () =>
      uniqBy(
        log.addedLegs.flatMap(({ resources }) => resources),
        'resourceId',
      ),
    [log.addedLegs],
  );

  const removedResources = useMemo(() => {
    const addedResourceIds = new Set(addedResources.map(({ resourceId }) => resourceId));

    return uniqBy(
      log.removedLegs.flatMap(({ resources }) => resources),
      'resourceId',
    ).filter(({ resourceId }) => !addedResourceIds.has(resourceId));
  }, [log.removedLegs, addedResources]);

  return (
    <>
      {!!addedLegs.length && (
        <>
          {hasResources && <ChangeLogResourcesList logType={log.$type} resources={addedResources} />}
          <ChangeLogRouteLocationsList logType={log.$type} legs={addedLegs} title="Added legs" />
        </>
      )}
      {!!removedLegs.length && (
        <>
          {hasResources && <ChangeLogResourcesList logType={log.$type} resources={removedResources} />}
          <ChangeLogRouteLocationsList logType={log.$type} legs={removedLegs} title="Removed legs" />
        </>
      )}
    </>
  );
};
